import React, { useEffect, useRef, useState } from "react";

import { useTrigger } from "shared/hooks/useTrigger";

import { PopoverEventerProps } from "../types";
import { eventListener } from "./utils";

const PopoverEventer: React.FC<PopoverEventerProps> = ({
  event,
  onClose,
  children,
  isOpenPopover,
}) => {
  const [value, on, off] = useTrigger(false);
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const close = (): void => {
    if (onClose) {
      onClose();
    }
    off();
  };

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    if (value) {
      if (event === "hover") {
        timeoutId = setTimeout(() => {
          if (value) {
            setIsOpen(true);
          }
        }, 300);
      } else {
        setIsOpen(true);
      }
    } else {
      setIsOpen(false);
    }

    return (): void => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [value, event]);

  const clickHandler = (e: Event): void => {
    const { target } = e;
    if (value && !popoverRef.current?.contains(target as Node)) {
      close();
    } else if (!value && triggerRef.current?.contains(target as Node)) {
      on();
    }
  };

  const focusHandler = (event: Event): void | false => {
    const { target } = event;

    if (popoverRef.current?.contains(target as Node)) {
      event.preventDefault();
    } else if (target === document.activeElement) {
      on();
    } else if (value) {
      close();
    }
  };

  const mouseHandler = ({ target, relatedTarget }: MouseEvent): void => {
    if (!value && triggerRef.current?.contains(target as Node)) {
      on();
    } else if (
      value &&
      (!(relatedTarget instanceof Node) ||
        // Not responding to mouseLeave, which goes to trigger or popover
        (!popoverRef.current?.contains(relatedTarget) &&
          !triggerRef.current?.contains(relatedTarget)))
    ) {
      close();
    }
  };

  useEffect(() => {
    if (isOpenPopover && !value) {
      on();
    } else if (!isOpenPopover && value) {
      off();
    }
  }, [isOpenPopover]);

  useEffect(() => {
    if (event !== "custom") {
      if (event === "hover") {
        return eventListener<MouseEvent>({
          targets: [triggerRef.current, popoverRef.current],
          events: ["mouseenter", "mouseleave"],
          handler: mouseHandler,
        });
      }

      if (event === "focus") {
        return eventListener({
          targets: [
            triggerRef.current?.querySelector(
              "input, textarea, button"
            ) as HTMLElement,
            popoverRef.current,
          ],
          events: [
            "focus",
            "blur",
            "ontouchstart" in document.documentElement
              ? "touchend"
              : "mousedown",
          ],
          handler: focusHandler,
        });
      }

      return eventListener({
        targets: [document],
        events: [
          ...("ontouchstart" in document.documentElement
            ? ["touchend", "touchmove"]
            : ["mousedown"]),
        ],
        handler: clickHandler,
      });
    }
  }, [triggerRef.current, popoverRef.current, value, isOpen]);

  return children({
    isOpen,
    triggerRef,
    popoverRef,
    close,
  });
};

export default PopoverEventer;
